import React, { useState, useEffect, useMemo, useCallback } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { XIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/outline';
import { functions } from '../../../firebase';
import { httpsCallable } from 'firebase/functions';
import { CLINICIAN_SPECIALISMS, CLINICIAN_SPECIALISM_LABELS } from '../../../constants/userTypes';

/**
 * RmsProviderAccountSetupForm
 * ---------------------------
 * Purpose:
 *   A step-by-step wizard dialog for creating new RMS provider accounts.
 *   This is the RMS-specific equivalent of the main platform's ProviderAccountSetupForm,
 *   with user types and fields tailored to the RMS workflow.
 *
 * Wizard Steps:
 *   1. Basic Information - First name, last name, email, user type
 *   2. Professional Details - Role, registration body, registration number
 *      (Skipped for non-clinical user types: Support, Non-Clinical Org. Admin)
 *
 * User Types (RMS-specific):
 *   - Clinician: Clinical staff who can be assigned to patients
 *   - Support: Non-clinical staff with limited permissions
 *   - Clinical Org. Admin: Organisation admin who is also clinical staff
 *   - Non-Clinical Org. Admin: Organisation admin who is not clinical staff
 *
 * Backend Mapping:
 *   The UI user types map to backend `userType` values as follows:
 *   - Clinician → userType: 'RMS Clinician', isAssignableClinician: true
 *   - Support → userType: 'RMS Support', isAssignableClinician: false
 *   - Clinical Org. Admin → userType: 'RMS Org. Admin', isAssignableClinician: true
 *   - Non-Clinical Org. Admin → userType: 'RMS Org. Admin', isAssignableClinician: false
 *
 * Props:
 *   - isOpen: boolean - Controls dialog visibility
 *   - onClose: () => void - Callback when dialog should close
 *   - orgId: string - The organisation ID for the new provider
 *   - onSuccess: () => void - Callback after successful account creation
 */

// UI user type options displayed in the wizard
const UI_USER_TYPES = [
  { value: 'Clinician', label: 'Clinician'},
  { value: 'Support', label: 'Support'},
  { value: 'Clinical Org. Admin', label: 'Clinical Org. Admin'},
  { value: 'Non-Clinical Org. Admin', label: 'Non-Clinical Org. Admin'},
];

// Registration body options (professional registration types)
const REGISTRATION_BODIES = [
  { value: 'HCPC', label: 'HCPC - Health and Care Professions Council' },
  { value: 'GMC', label: 'GMC - General Medical Council' },
  { value: 'NMC', label: 'NMC - Nursing and Midwifery Council' },
  { value: 'BPS', label: 'BPS - British Psychological Society' },
  { value: 'BACP', label: 'BACP - British Association for Counselling and Psychotherapy' },
  { value: 'Other', label: 'Other' },
];

/**
 * Maps the UI user type selection to backend `userType` and `isAssignableClinician` values.
 * This mapping ensures the backend can properly enforce role-based access control.
 */
function mapUiUserTypeToBackend(uiUserType) {
  switch (uiUserType) {
    case 'Clinician':
      return { userType: 'RMS Clinician', isAssignableClinician: true };
    case 'Support':
      return { userType: 'RMS Support', isAssignableClinician: false };
    case 'Clinical Org. Admin':
      return { userType: 'RMS Org. Admin', isAssignableClinician: true };
    case 'Non-Clinical Org. Admin':
      return { userType: 'RMS Org. Admin', isAssignableClinician: false };
    default:
      return { userType: null, isAssignableClinician: null };
  }
}

/**
 * Determines if the professional details step should be skipped based on user type.
 * Non-clinical user types (Support, Non-Clinical Org. Admin) don't need registration details.
 */
function shouldSkipProfessionalStep(userType) {
  return userType === 'Support' || userType === 'Non-Clinical Org. Admin';
}

export default function RmsProviderAccountSetupForm({ isOpen, onClose, orgId, onSuccess }) {
  // ─────────────────────────────────────────────────────────────────────────────
  // State Management
  // ─────────────────────────────────────────────────────────────────────────────

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');

  // Form field state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [userType, setUserType] = useState('');
  const [specialism, setSpecialism] = useState('');
  const [role, setRole] = useState('');
  const [registrationBody, setRegistrationBody] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');

  // ─────────────────────────────────────────────────────────────────────────────
  // Derived State
  // ─────────────────────────────────────────────────────────────────────────────

  // Determine if the professional details step should be skipped
  const skipProfessionalStep = useMemo(() => shouldSkipProfessionalStep(userType), [userType]);

  // Clinicians and Support users carry an ASD/ADHD/both specialism — it scopes
  // the triage queue they see the same way for both roles ('both' = all
  // pathways). Support cannot triage, but their queue is pathway-scoped too.
  const requiresSpecialism = useMemo(
    () => userType === 'Clinician' || userType === 'Support',
    [userType],
  );

  // Calculate total steps (2 for clinical, 1 for non-clinical)
  const totalSteps = useMemo(() => (skipProfessionalStep ? 1 : 2), [skipProfessionalStep]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Reset Form
  // ─────────────────────────────────────────────────────────────────────────────

  const resetForm = useCallback(() => {
    setCurrentStep(1);
    setFirstName('');
    setLastName('');
    setEmail('');
    setUserType('');
    setSpecialism('');
    setRole('');
    setRegistrationBody('');
    setRegistrationNumber('');
    setSubmitError('');
    setSubmitSuccess('');
    setIsSubmitting(false);
  }, []);

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Validation
  // ─────────────────────────────────────────────────────────────────────────────

  const validateEmail = useCallback((emailValue) => {
    if (!emailValue) return true;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailValue);
  }, []);

  // Validate the current step
  const isCurrentStepValid = useMemo(() => {
    if (currentStep === 1) {
      const isEmailValid = email ? validateEmail(email) : false;
      const specialismOk = !requiresSpecialism || specialism.length > 0;
      return (
        firstName.trim().length > 0 &&
        lastName.trim().length > 0 &&
        isEmailValid &&
        userType.length > 0 &&
        specialismOk
      );
    }

    if (currentStep === 2) {
      // Professional step - role is optional, registration details are optional but if one is provided both must be
      if (registrationBody && !registrationNumber) return false;
      if (registrationNumber && !registrationBody) return false;
      return true;
    }

    return false;
  }, [currentStep, firstName, lastName, email, userType, requiresSpecialism, specialism, registrationBody, registrationNumber, validateEmail]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Submission
  // ─────────────────────────────────────────────────────────────────────────────

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    setSubmitError('');
    setSubmitSuccess('');

    try {
      // Map UI user type to backend values
      const mapped = mapUiUserTypeToBackend(userType);
      if (!mapped.userType) {
        throw new Error('Invalid user type selected');
      }

      // Call the Cloud Function to create the RMS provider account
      const rmsSetupNewProviderFn = httpsCallable(functions, 'rmsSetupNewProvider');

      const result = await rmsSetupNewProviderFn({
        orgId,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.toLowerCase().trim(),
        userType: mapped.userType,
        isAssignableClinician: mapped.isAssignableClinician,
        // Specialism scopes the triage queue for clinicians and support; omit
        // it for other roles (e.g. Org. Admin).
        specialism: ['RMS Clinician', 'RMS Support'].includes(mapped.userType) ? specialism : null,
        role: role.trim() || null,
        registrationBody: registrationBody || null,
        registrationNumber: registrationNumber.trim() || null,
      });

      if (result?.data?.success) {
        setSubmitSuccess('Invitation sent successfully. The user will receive an email with sign-in instructions.');
        
        if (onSuccess) {
          onSuccess();
        }

        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        throw new Error(result?.data?.message || 'Failed to send invitation');
      }
    } catch (error) {
      console.error('[RmsProviderAccountSetupForm] Error:', error);

      // Handle specific error cases
      if (error?.code === 'functions/already-exists' || error?.message?.includes('already exists')) {
        setSubmitError('A user with this email already exists. Please use a different email address.');
      } else if (error?.code === 'functions/invalid-argument') {
        setSubmitError(error.message || 'Invalid input. Please check your form data.');
      } else {
        setSubmitError(error?.message || 'An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [orgId, firstName, lastName, email, userType, specialism, role, registrationBody, registrationNumber, onSuccess, onClose]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Navigation
  // ─────────────────────────────────────────────────────────────────────────────

  const handleNext = useCallback(async () => {
    if (currentStep < totalSteps) {
      setCurrentStep((prev) => prev + 1);
    } else {
      // Final step - submit the form
      await handleSubmit();
    }
  }, [currentStep, totalSteps, handleSubmit]);

  const handleBack = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Step Content Rendering
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Step 1: Basic Information
   */
  const renderStep1 = () => (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">Basic Information</h3>
        <p className="text-sm text-gray-500 mb-4">Enter the new user's details</p>
      </div>

      {/* First Name */}
      <div>
        <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
          First Name <span className="text-red-500">*</span>
        </label>
        <input
          id="firstName"
          type="text"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value.replace(/[^a-zA-Z\s\-']/g, ''))}
          placeholder="Enter first name"
          className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        />
      </div>

      {/* Last Name */}
      <div>
        <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
          Last Name <span className="text-red-500">*</span>
        </label>
        <input
          id="lastName"
          type="text"
          value={lastName}
          onChange={(e) => setLastName(e.target.value.replace(/[^a-zA-Z\s\-']/g, ''))}
          placeholder="Enter last name"
          className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        />
      </div>

      {/* Email */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
          Email <span className="text-red-500">*</span>
        </label>
        <input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter email address"
          className={`w-full p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
            email && !validateEmail(email) ? 'border-red-300' : 'border-gray-300'
          }`}
        />
        {email && !validateEmail(email) && (
          <p className="text-xs text-red-500 mt-1">Please enter a valid email address</p>
        )}
      </div>

      {/* User Type */}
      <div>
        <label htmlFor="userType" className="block text-sm font-medium text-gray-700 mb-1">
          User Type <span className="text-red-500">*</span>
        </label>
        <select
          id="userType"
          value={userType}
          onChange={(e) => {
            const next = e.target.value;
            setUserType(next);
            // Specialism applies to clinicians and support (both are queue-scoped);
            // clear it for roles that don't carry one (e.g. Org. Admin).
            if (next !== 'Clinician' && next !== 'Support') setSpecialism('');
          }}
          className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white"
        >
          <option value="">Select user type...</option>
          {UI_USER_TYPES.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
      </div>

      {/* Specialism (clinicians only) — drives the triage team / queue access */}
      {requiresSpecialism && (
        <div>
          <label htmlFor="specialism" className="block text-sm font-medium text-gray-700 mb-1">
            Triage specialism <span className="text-red-500">*</span>
          </label>
          <select
            id="specialism"
            value={specialism}
            onChange={(e) => setSpecialism(e.target.value)}
            className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white"
          >
            <option value="">Select specialism...</option>
            {CLINICIAN_SPECIALISMS.map((value) => (
              <option key={value} value={value}>
                {CLINICIAN_SPECIALISM_LABELS[value]}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Determines which triage queue(s) this user can access.
          </p>
        </div>
      )}
    </div>
  );

  /**
   * Step 2: Professional Details (Clinical users only)
   */
  const renderStep2 = () => (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">Professional Details</h3>
        <p className="text-sm text-gray-500 mb-4">
          Optional professional registration information
        </p>
      </div>

      {/* Role */}
      <div>
        <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
          Role
        </label>
        <input
          id="role"
          type="text"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          placeholder="e.g., Clinical Psychologist, Psychiatrist"
          className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        />
      </div>

      {/* Registration Body */}
      <div>
        <label htmlFor="registrationBody" className="block text-sm font-medium text-gray-700 mb-1">
          Registration Body
        </label>
        <select
          id="registrationBody"
          value={registrationBody}
          onChange={(e) => setRegistrationBody(e.target.value)}
          className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white"
        >
          <option value="">Select registration body...</option>
          {REGISTRATION_BODIES.map((body) => (
            <option key={body.value} value={body.value}>
              {body.label}
            </option>
          ))}
        </select>
      </div>

      {/* Registration Number */}
      <div>
        <label htmlFor="registrationNumber" className="block text-sm font-medium text-gray-700 mb-1">
          Registration Number
        </label>
        <input
          id="registrationNumber"
          type="text"
          value={registrationNumber}
          onChange={(e) => setRegistrationNumber(e.target.value)}
          placeholder="Enter registration number"
          className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
        />
        {(registrationBody && !registrationNumber) || (!registrationBody && registrationNumber) ? (
          <p className="text-xs text-amber-600 mt-1">
            If providing registration details, both body and number are required
          </p>
        ) : null}
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/40 backdrop-blur-sm z-50" />
        <Dialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 max-h-[85vh] w-[90vw] max-w-md bg-white rounded-xl shadow-lg z-50 overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-start justify-between p-5 pb-0">
            <div>
              <Dialog.Title className="text-xl font-semibold text-gray-900">
                Set Up New User
              </Dialog.Title>
              <Dialog.Description className="text-sm text-gray-500 mt-0.5">
                Send an invitation to set up a new RMS provider account
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                aria-label="Close dialog"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </Dialog.Close>
          </div>

          {/* Progress indicator */}
          <div className="px-5 pt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500">
                Step {currentStep} of {totalSteps}
              </span>
            </div>
            <div className="flex h-1.5 rounded-full bg-gray-200">
              <div
                className="h-full bg-primary rounded-full transition-all duration-300 ease-in-out"
                style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              />
            </div>
          </div>

          {/* Content */}
          <div className="flex-grow overflow-y-auto p-5" style={{ maxHeight: '55vh' }}>
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
          </div>

          {/* Error/Success messages */}
          {submitError && (
            <div className="mx-5 mb-3 p-3 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg">
              {submitError}
            </div>
          )}
          {submitSuccess && (
            <div className="mx-5 mb-3 p-3 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg">
              {submitSuccess}
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-between gap-3 p-5 pt-3 border-t border-gray-100">
            {currentStep > 1 ? (
              <button
                type="button"
                onClick={handleBack}
                disabled={isSubmitting}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium flex items-center disabled:opacity-50"
              >
                <ChevronLeftIcon className="w-4 h-4 mr-1" />
                Back
              </button>
            ) : (
              <Dialog.Close asChild>
                <button
                  type="button"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium disabled:opacity-50"
                >
                  Cancel
                </button>
              </Dialog.Close>
            )}

            <button
              type="button"
              onClick={handleNext}
              disabled={!isCurrentStepValid || isSubmitting}
              className={`px-4 py-2 rounded-lg transition-colors text-sm font-medium flex items-center ${
                isCurrentStepValid && !isSubmitting
                  ? 'bg-primary text-white hover:bg-primary/90'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Sending Invite...
                </>
              ) : currentStep === totalSteps ? (
                'Send Invite'
              ) : (
                <>
                  Next
                  <ChevronRightIcon className="w-4 h-4 ml-1" />
                </>
              )}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
